// src/pages/Splash.tsx
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { storage } from "../lib/storage";
import { logError, toUserMessage, AppError } from "../lib/errors";
import { bootstrapAuth, BootstrapPhase } from "../services/auth";
import { api } from "../lib/api";
import { GOOGLE_CLIENT_ID, GOOGLE_SSO_BYPASS } from "../lib/config";
import { useSsoFlow, type SsoLoginResponse } from "../hooks/useSsoFlow";
import { InviteCodeInputCard } from "../components/InviteCodeInputCard";

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
          }) => void;
          renderButton: (element: HTMLElement, options: object) => void;
        };
      };
    };
  }
}

type Phase =
  | "initializing"
  | "googleSignIn"
  | "googleVerifying"
  | BootstrapPhase
  | "navigating"
  | "error"
  | "forbidden";

const GOOGLE_GSI_SCRIPT_SELECTOR = 'script[data-google-gsi-script="true"]';
const FORCE_NEW_USER_BOOTSTRAP_KEY = "wizmate.force-new-user-bootstrap";
const FORCE_NEW_USER_BOOTSTRAP_TTL_MS = 5 * 60 * 1000;

let googleGsiLoader: Promise<void> | null = null;

function getGoogleAccountsId() {
  return window.google?.accounts?.id ?? null;
}

function loadGoogleIdentityServices(): Promise<void> {
  if (getGoogleAccountsId()) {
    return Promise.resolve();
  }

  if (!googleGsiLoader) {
    googleGsiLoader = new Promise((resolve, reject) => {
      let script = document.querySelector<HTMLScriptElement>(GOOGLE_GSI_SCRIPT_SELECTOR);

      if (script?.dataset.loaded === "error") {
        script.remove();
        script = null;
      }

      const targetScript = script ?? document.createElement("script");

      const cleanup = () => {
        targetScript.removeEventListener("load", handleLoad);
        targetScript.removeEventListener("error", handleError);
      };

      const fail = (message: string) => {
        cleanup();
        googleGsiLoader = null;
        reject(new Error(message));
      };

      const handleLoad = () => {
        targetScript.dataset.loaded = "true";
        cleanup();
        if (getGoogleAccountsId()) {
          resolve();
          return;
        }
        googleGsiLoader = null;
        reject(new Error("Google Identity Services did not initialize."));
      };

      const handleError = () => {
        targetScript.dataset.loaded = "error";
        fail("Failed to load Google Identity Services.");
      };

      if (getGoogleAccountsId()) {
        resolve();
        return;
      }

      if (targetScript.dataset.loaded === "true") {
        fail("Google Identity Services did not initialize.");
        return;
      }

      targetScript.addEventListener("load", handleLoad);
      targetScript.addEventListener("error", handleError);

      if (!script) {
        targetScript.src = "https://accounts.google.com/gsi/client";
        targetScript.async = true;
        targetScript.defer = true;
        targetScript.dataset.googleGsiScript = "true";
        targetScript.dataset.loaded = "loading";
        document.head.appendChild(targetScript);
      }
    });
  }

  return googleGsiLoader;
}

function consumeForceNewUserBootstrapFlag(): boolean {
  const raw = localStorage.getItem(FORCE_NEW_USER_BOOTSTRAP_KEY);
  if (!raw) {
    return false;
  }

  localStorage.removeItem(FORCE_NEW_USER_BOOTSTRAP_KEY);

  const requestedAt = Number(raw);
  if (!Number.isFinite(requestedAt)) {
    return false;
  }

  return Date.now() - requestedAt <= FORCE_NEW_USER_BOOTSTRAP_TTL_MS;
}

export default function Splash() {
  const navigate = useNavigate();

  const [phase, setPhase] = useState<Phase>("initializing");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const runningRef = useRef(false);
  const googleBtnRef = useRef<HTMLDivElement>(null);

  const runBootstrap = useCallback(async () => {
    try {
      setErrorMessage(null);
      await bootstrapAuth({
        onPhase: (p: BootstrapPhase) => setPhase(p),
      });
      setPhase("navigating");
      navigate("/home", { replace: true });
    } catch (e) {
      if (e instanceof AppError && e.code === "SESSION_EXPIRED") {
        // SSO の accessToken 期限切れ: SSO セッションをクリアして Google サインイン画面へ
        storage.clearGoogleSso();
        setPhase("googleSignIn");
        runningRef.current = false;
        return;
      }
      const code = e instanceof AppError ? e.code : "NON_APP_ERROR";
      const msg = e instanceof Error ? e.message : String(e);
      const meta = e instanceof AppError ? JSON.stringify(e.meta) : "";
      const detail = `[${code}] ${msg}\n${meta}`;
      console.error("[Splash] runBootstrap error:", detail);
      logError(e);
      setPhase("error");
      setErrorMessage(toUserMessage(e));
      runningRef.current = false;
    }
  }, [navigate]);

  const handleSsoSuccess = useCallback(async (ssoResponse: SsoLoginResponse) => {
    console.log("[Splash] googleSsoLogin response:", JSON.stringify({
      email: ssoResponse.email,
      userId: ssoResponse.userId,
      hasSecretKey: !!ssoResponse.secretKey,
      hasAccessToken: !!ssoResponse.accessToken,
      isNewUser: ssoResponse.isNewUser,
    }));
    const { email, userId, secretKey, accessToken } = ssoResponse;
    storage.setGoogleSso(email);

    const shouldForceNewUserBootstrap = consumeForceNewUserBootstrapFlag();
    const shouldSkipSsoSessionRestore =
      shouldForceNewUserBootstrap && ssoResponse.isNewUser === true;

    // バックエンドが userId を返した場合はそのアカウントで上書きする。
    // 同じ Google アカウントでサインインすれば常に同じ userId が返るため、
    // PC・スマホ間でアカウントが統一される。
    console.log("[Splash] userId && accessToken check:", { userId, hasAccessToken: !!accessToken });
    if (!shouldSkipSsoSessionRestore && userId && accessToken) {
      const currentAuth = storage.getAuth();
      const isSameUser = currentAuth.userId === userId;
      const effectiveSecretKey = secretKey ?? (isSameUser ? currentAuth.secretKey : undefined);

      storage.setAuth({
        userId,
        secretKey: effectiveSecretKey,
        accessToken,
        // ユーザーが切り替わる場合は選択状態を初期化する。
        currentCharacterId: isSameUser ? currentAuth.currentCharacterId : undefined,
        currentVoiceId: isSameUser ? currentAuth.currentVoiceId : undefined,
        terminalIdentificationNumber: currentAuth.terminalIdentificationNumber,
      });
      console.log("[Splash] setAuth done with userId:", userId);
    } else {
      console.log("[Splash] skipped setAuth — falling through to bootstrapAuth (may create new user)");
    }

    await runBootstrap();
  }, [runBootstrap]);

  const { ssoFlowPhase, inviteCodeError, inviteCodeLoading, startSsoLogin, submitInviteCode, cancelInviteCode } =
    useSsoFlow(handleSsoSuccess);

  const handleGoogleCredential = useCallback(async (credential: string) => {
    if (runningRef.current) return;
    runningRef.current = true;

    setPhase("googleVerifying");
    setErrorMessage(null);

    try {
      const inviteCodeRequired = await startSsoLogin(credential);
      if (inviteCodeRequired) {
        // 招待コード入力フェーズへ遷移。runningRef は true のまま（Google ボタンを無効化）
        setPhase("googleSignIn");
      }
    } catch (e) {
      if (e instanceof AppError && e.code === "FORBIDDEN") {
        setPhase("forbidden");
        setErrorMessage("このアカウントはアクセスが許可されていません。");
      } else {
        logError(e);
        setPhase("googleSignIn");
        setErrorMessage(toUserMessage(e));
      }
      runningRef.current = false;
    }
  }, [startSsoLogin]);

  const handleCancelInviteCode = useCallback(() => {
    cancelInviteCode();
    runningRef.current = false;
  }, [cancelInviteCode]);

  useEffect(() => {
    // 開発用バイパス（REACT_APP_GOOGLE_SSO_BYPASS=true のとき SSO をスキップ）
    if (GOOGLE_SSO_BYPASS) {
      runningRef.current = true;
      void runBootstrap();
      return;
    }

    // 有効な SSO セッションがあればそのまま起動
    if (storage.isGoogleSsoValid()) {
      runningRef.current = true;
      void runBootstrap();
      return;
    }

    if (!GOOGLE_CLIENT_ID) {
      setPhase("error");
      setErrorMessage("Google Client IDが設定されていません。");
      return;
    }

    setPhase("googleSignIn");
  }, [runBootstrap]);

  useEffect(() => {
    if (phase !== "googleSignIn" || !GOOGLE_CLIENT_ID) {
      return;
    }

    let disposed = false;

    const renderGoogleButton = async () => {
      try {
        await loadGoogleIdentityServices();

        if (disposed) return;

        const googleAccountsId = getGoogleAccountsId();
        const buttonContainer = googleBtnRef.current;
        if (!googleAccountsId || !buttonContainer) return;

        buttonContainer.innerHTML = "";
        googleAccountsId.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: ({ credential }) => {
            void handleGoogleCredential(credential);
          },
        });
        googleAccountsId.renderButton(buttonContainer, {
          theme: "outline",
          size: "large",
          locale: "ja",
        });
      } catch (e) {
        if (disposed) return;
        logError(e);
        setPhase("error");
        setErrorMessage("Googleでログインの準備に失敗しました。ページを再読み込みして、もう一度お試しください。");
        runningRef.current = false;
      }
    };

    void renderGoogleButton();

    return () => {
      disposed = true;
    };
  }, [phase, handleGoogleCredential]);

  const onInitialize = async () => {
    const ok = window.confirm("初期化します。よろしいですか？");
    if (!ok) return;

    const auth = storage.getAuth();
    if (auth.userId && auth.accessToken) {
      try {
        await api.invalidateGoogleSso({ reason: "user_initialize" });
      } catch (e) {
        logError(e);
      }
    }

    storage.clearAuth();
    storage.clearGoogleSso();
    localStorage.removeItem("app_device_id");
    localStorage.setItem(FORCE_NEW_USER_BOOTSTRAP_KEY, String(Date.now()));

    // 実行中の認証処理や非同期状態を確実に破棄するため、Splash を再読み込みする。
    window.location.replace("/splash");
  };

  const handleRetry = () => {
    if (phase !== "error") return;
    runningRef.current = true;
    void runBootstrap();
  };

  const phaseLabel =
    phase === "googleVerifying"
      ? "認証確認中..."
      : phase === "creatingUser"
        ? "ユーザ作成中..."
        : phase === "loggingIn"
          ? "ログイン中..."
          : phase === "eventLogin"
            ? "ログイン処理中..."
            : phase === "checking"
              ? "確認中..."
              : phase === "navigating"
                ? "起動中."
                : "";

  const isLoading =
    phase === "initializing" ||
    phase === "googleVerifying" ||
    phase === "checking" ||
    phase === "creatingUser" ||
    phase === "loggingIn" ||
    phase === "eventLogin" ||
    phase === "navigating";

  const canInitialize = phase !== "navigating";

  return (
    <main className="min-h-screen bg-[#FDF8F5] text-[#4A3E39] antialiased flex items-center justify-center px-6 select-none">
      <div className="w-full max-w-sm">
        <div className="relative overflow-hidden rounded-[2.2rem] border border-[#EADBC8] bg-white/70 shadow-[0_14px_28px_rgba(166,124,82,0.16)] backdrop-blur-sm">
          <div className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-[#F4E3D7] opacity-70 blur-3xl" />

          <div className="absolute right-4 top-4 flex gap-2">
            {canInitialize && (
              <button
                type="button"
                className="rounded-full border border-[#EADBC8] bg-white/80 px-3 py-1 text-[11px] font-bold text-[#A67C52]"
                onClick={onInitialize}
                aria-label="Initialize"
              >
                初期化
              </button>
            )}
          </div>

          <div className="px-8 pb-10 pt-12 text-center">
            <p className="text-[11px] font-bold tracking-[0.12em] text-[#A67C52]">PERSONAL CONCIERGE</p>
            <h1 className="mt-1 text-3xl font-black tracking-tight text-[#7C5A4E]">WizMate</h1>

            <div className="mx-auto mb-6 mt-6 h-40 w-40 overflow-hidden rounded-full border-[6px] border-white bg-white shadow-[0_10px_25px_rgba(166,124,82,0.2)]">
              <img
                src="/img/logo.jpg"
                alt="WizMate Logo"
                className="h-full w-full object-cover object-center"
              />
            </div>

            <p className="text-sm font-medium text-[#7B6861]">あなた専属コンシェルジュを起動します</p>

            {phase === "googleSignIn" && ssoFlowPhase === "inviteCodeInput" && (
              <InviteCodeInputCard
                onSubmit={submitInviteCode}
                onCancel={handleCancelInviteCode}
                error={inviteCodeError}
                loading={inviteCodeLoading}
              />
            )}

            {phase === "googleSignIn" && ssoFlowPhase === "idle" && (
              <div className="mt-10">
                {errorMessage && (
                  <p className="mb-4 rounded-2xl border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-xs font-medium text-[#A1493E]">
                    {errorMessage}
                  </p>
                )}
                <p className="mb-4 text-sm font-medium text-[#7B6861]">Googleアカウントでサインインしてください</p>
                <div className="flex justify-center">
                  <div ref={googleBtnRef} />
                </div>
              </div>
            )}

            {isLoading && (
              <div className="mt-10 rounded-2xl border border-[#EADBC8] bg-white/70 px-4 py-4">
                <p className="text-sm font-bold text-[#5D4D46]">{phaseLabel}</p>
                <div className="mt-4 flex items-center justify-center gap-1.5" aria-label="loading">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#D1A48F] animate-bounce [animation-delay:-0.2s]" />
                  <span className="h-1.5 w-1.5 rounded-full bg-[#D1A48F] animate-bounce [animation-delay:-0.1s]" />
                  <span className="h-1.5 w-1.5 rounded-full bg-[#D1A48F] animate-bounce" />
                </div>
              </div>
            )}

            {phase === "forbidden" && (
              <div className="mt-8 rounded-2xl border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-4 text-left">
                <p className="text-sm font-bold text-[#A1493E]">アクセスが許可されていません</p>
                <p className="mt-1 text-xs text-[#A1493E]">{errorMessage}</p>
                <p className="mt-3 text-xs font-medium text-[#6B5851]">
                  アクセス権限については管理者にお問い合わせください。
                </p>
              </div>
            )}

            {phase === "error" && (
              <div className="mt-8 rounded-2xl border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-4 text-left">
                <p className="text-sm font-bold text-[#A1493E]">起動処理に失敗しました</p>
                <p className="mt-1 break-words text-xs text-[#A1493E]">{errorMessage}</p>
                <button
                  className="mt-3 w-full rounded-xl border border-[#EADBC8] bg-white px-4 py-2 text-sm font-bold text-[#5D4D46] hover:bg-[#FDF8F5] active:scale-[0.99]"
                  onClick={handleRetry}
                >
                  再試行
                </button>
              </div>
            )}
          </div>

          <div className="border-t border-[#EADBC8] px-6 py-4 text-center">
            <p className="text-[11px] text-[#9C867D]">© WizMate</p>
          </div>
        </div>
      </div>
    </main>
  );
}
